import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Flag, ShieldQuestion, X } from 'lucide-react'

import { useLocalAuth } from '@/auth/LocalAuthContext'
import { canEditModule } from '@/auth/modulePermission'
import DialogShell from '@/components/crm/DialogShell'
import SourcePreviewPane from '@/components/intake/SourcePreviewPane'
import SourceViewerDialog from '@/components/intake/SourceViewerDialog'
import {
  dataReview,
  dataReviewKeys,
  invalidateAfterDecision,
  refusalOf,
  type DataReviewDecisionInput,
  type DataReviewItem,
  type DataReviewRecord,
  type DataReviewRefusal,
} from '@/services/dataReview'
import {
  ingestionService,
  type LocatedCandidate,
  type Provenance,
  type ReviewCandidate,
  type ReviewItem,
  type SearchedDoc,
  type SourceAnchor,
  type SourceFileRef,
} from '@/services/ingestion'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { DatapointPanel } from './DatapointPanel'
import { OverridePrompt } from './DecisionBar'
import { DiscoveredRow } from './DiscoveredRow'
import type { EvidenceTarget } from './discoveryEvidence'
import { NeedsPersonPanel } from './NeedsPersonPanel'
import { ReparseItemPanel } from './ReparseItemPanel'
import { SOURCE_LABEL, describeResult, isOwed, kindMeta, plural, previewTarget, valueText } from './dataReviewFormat'

/**
 * Data Intelligence Review — the one place a person answers what the platform
 * could not settle on its own.
 *
 * Opened on a deal (from a blank pipeline cell, or the Deal Inspector) or on an
 * intake record, it lists every open question ranked — the checklist's
 * conflicts and unconfirmed values, what the engine now reads differently, what
 * only a person can settle on the promoted deal, and the labels no field holds
 * yet — and answers each with the same three verbs: Confirm, Correct (pick,
 * type, or point at the document), Not available. A found datapoint is added or
 * dismissed.
 *
 * Every answer applies to ONE row and is recorded against the reviewer's name
 * with the evidence it rests on. There is no control that answers several, a
 * value a person decided is never overwritten without an explicit, audited
 * overrule, and nothing is applied unless somebody pressed its button.
 *
 * It replaced three screens that each had their own list, panel and write
 * route — the per-record checklist, the re-parse proposals and the found
 * datapoints — and a pipeline marker and an Inspector line that only mentioned
 * them in copy.
 */

type Answer = Omit<DataReviewDecisionInput, 'itemId' | 'note' | 'overrideHumanDecision'>

/** A checklist row and a re-parse proposal for one field are the same question
 *  asked twice; the checklist's is shown first when a caller names a field. */
const SOURCE_ORDER: DataReviewItem['source'][] = ['review', 'reparse', 'reconcile', 'discovery']

/** The checklist calls the deal's name `deal_name`; the engine's proposals call
 *  it `property_name`. Asked for one, the panel opens on either. */
const FIELD_ALIASES: Record<string, string[]> = {
  deal_name: ['property_name'],
  property_name: ['deal_name'],
}

type Viewer = {
  ingestionId: string
  files: SourceFileRef[]
  sha: string
  anchor: SourceAnchor | null
  /** Set when the viewer is open to point the engine at a value. */
  designate: { itemId: string; field: string; label: string } | null
}

export interface DataReviewPanelProps {
  /** Everything open on this deal, across every record it was promoted from. */
  loanId?: string
  /** One intake record. Wins over `loanId` when both are given. */
  ingestionId?: string
  /** Open on this row… */
  initialItemId?: string
  /** …or on the first open row for this field (the checklist's name for it). */
  initialField?: string
  onClose: () => void
}

function RailRow({
  item, active, step, total, onSelect,
}: {
  item: DataReviewItem
  active: boolean
  step: number | undefined
  total: number
  onSelect: () => void
}) {
  const meta = kindMeta(item.kind)
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full items-start gap-2 border-l-2 px-3 py-1.5 text-left transition ${
        active ? 'border-amber-400 bg-amber-400/[0.07]' : 'border-transparent hover:bg-white/[0.03]'
      }`}
    >
      {step != null ? (
        <span
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold tabular-nums ${
            active ? 'bg-amber-400 text-stone-950' : 'bg-amber-400/20 text-amber-200 ring-1 ring-amber-400/40'
          }`}
          aria-label={`Outstanding item ${step} of ${total}`}
        >
          {step}
        </span>
      ) : (
        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="truncate text-[11px] text-stone-200">{item.label}</span>
          {item.pipelineCritical && item.kind !== 'decided' && (
            <Flag size={8} className="shrink-0 text-amber-400" aria-label="Shown on the pipeline" />
          )}
        </span>
        <span className="block truncate font-mono text-[10px] text-stone-500">
          <span className="font-sans text-stone-600">{SOURCE_LABEL[item.source]}</span> · {valueText(item)}
        </span>
      </span>
      {item.kind === 'decided' ? (
        <Check size={10} className="mt-1 shrink-0 text-emerald-400" />
      ) : (
        <span className={`mt-1 shrink-0 rounded border px-1 text-[8px] uppercase ${meta.cls}`}>{meta.label}</span>
      )}
    </button>
  )
}

function RailHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-0.5 pt-2 text-[9px] uppercase tracking-widest text-stone-600">{children}</div>
  )
}

/**
 * A record's open rows that are not owed, grouped by what the server says each
 * one is (`bucketNote`), in the order the first of each kind was ranked. The
 * note is said once above its group: five values no document states read as one
 * reason and five rows, not the same sentence five times.
 */
function byNote(items: DataReviewItem[]): { note: string | null; items: DataReviewItem[] }[] {
  const groups: { note: string | null; items: DataReviewItem[] }[] = []
  for (const item of items) {
    const note = item.bucketNote ?? null
    const group = groups.find((g) => g.note === note)
    if (group) group.items.push(item)
    else groups.push({ note, items: [item] })
  }
  return groups
}

export default function DataReviewPanel({
  loanId, ingestionId, initialItemId, initialField, onClose,
}: DataReviewPanelProps) {
  const queryClient = useQueryClient()
  const { localUser } = useLocalAuth()

  const recordQuery = useQuery({
    queryKey: dataReviewKeys.record(ingestionId ?? ''),
    queryFn: () => dataReview.record(ingestionId!),
    enabled: Boolean(ingestionId),
  })
  const loanQuery = useQuery({
    queryKey: dataReviewKeys.loan(loanId ?? ''),
    queryFn: () => dataReview.byLoan(loanId!),
    enabled: !ingestionId && Boolean(loanId),
  })
  const query = ingestionId ? recordQuery : loanQuery

  const records: DataReviewRecord[] = useMemo(
    () => (ingestionId ? (recordQuery.data ? [recordQuery.data] : []) : (loanQuery.data?.deals ?? [])),
    [ingestionId, recordQuery.data, loanQuery.data],
  )
  const discovery = ingestionId ? recordQuery.data?.discovery : loanQuery.data?.discovery
  const title = (ingestionId ? recordQuery.data?.property : loanQuery.data?.property)
    ?? records[0]?.subject ?? null
  // A deal made by hand, or promoted before its record kept the link, has no
  // package to read. Nothing to read is not the same as everything answered.
  const noRecord = !ingestionId && records.length === 0

  // The rail's order is the navigation order: each record's decisions owed, then
  // its open rows that are not owed (grouped by why), then what a person already
  // decided there, then the found datapoints. Only the decisions owed are
  // numbered and counted — what the inbox's Open column and the Intake badge
  // count — so a record does not read as 4 owed in the inbox and 9 open here.
  const groups = useMemo(() => records.map((record) => {
    const open = record.items.filter((i) => i.kind !== 'decided')
    return {
      record,
      owed: open.filter(isOwed),
      notOwed: byNote(open.filter((i) => !isOwed(i))),
      decided: record.items.filter((i) => i.kind === 'decided'),
    }
  }), [records])
  const found = useMemo(() => (discovery?.available ? discovery.items : []), [discovery])
  const owedItems = useMemo(() => groups.flatMap((g) => g.owed), [groups])
  const openItems = useMemo(
    () => [...groups.flatMap((g) => [...g.owed, ...g.notOwed.flatMap((n) => n.items)]), ...found],
    [groups, found],
  )
  const allItems = useMemo(
    () => [...groups.flatMap((g) => [...g.owed, ...g.notOwed.flatMap((n) => n.items), ...g.decided]), ...found],
    [groups, found],
  )
  const stepOf = useMemo(() => new Map(owedItems.map((item, index) => [item.id, index + 1])), [owedItems])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  // The caller's hint says where to START; once the reviewer has moved or
  // decided anything, the panel never jumps back to it.
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The row a refused decision was refused for: a found datapoint's refusal
  // is said on its card, where the decision was made.
  const [errorFor, setErrorFor] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [pending, setPending] = useState<{ item: DataReviewItem; answer: Answer; refusal: DataReviewRefusal } | null>(null)
  const [decidedCount, setDecidedCount] = useState(0)
  const [note, setNote] = useState('')
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({})
  const [reasonDraft, setReasonDraft] = useState<Record<string, string>>({})
  const [located, setLocated] = useState<Record<string, LocatedCandidate[]>>({})
  const [searchedIn, setSearchedIn] = useState<Record<string, SearchedDoc[]>>({})
  const [searching, setSearching] = useState<string | null>(null)
  const [scopeSha, setScopeSha] = useState('')
  const [viewer, setViewer] = useState<Viewer | null>(null)

  const start = useMemo(() => {
    if (initialItemId) {
      const named = allItems.find((i) => i.id === initialItemId)
      if (named) return named
    }
    if (initialField) {
      const names = [initialField, ...(FIELD_ALIASES[initialField] ?? [])]
      for (const source of SOURCE_ORDER) {
        const row = openItems.find((i) => i.source === source && i.field != null && names.includes(i.field))
        if (row) return row
      }
    }
    return null
  }, [allItems, openItems, initialItemId, initialField])
  const active = allItems.find((i) => i.id === selectedId)
    ?? (!touched ? start : null) ?? owedItems[0] ?? openItems[0] ?? allItems[0] ?? null
  // A row that is open but not owed is still answerable; the screen says why it
  // is not counted rather than leave its card to read like the rest.
  const activeNotOwed = active != null && active.source !== 'discovery' && active.kind !== 'decided'
    && !isOwed(active)
  const activeIndex = active ? allItems.findIndex((i) => i.id === active.id) : -1
  const activeRecord = active ? records.find((r) => r.ingestionId === active.ingestionId) : undefined
  const sourceFiles = activeRecord?.rawFilePaths ?? []
  // The server refuses a verb to a demo record and to a role that cannot edit;
  // the screen says so up front instead of letting a reviewer type an answer it
  // will turn away. Presentation only — the API is the authority either way.
  const mayDecide = canEditModule(localUser, 'intake')
  const mayAddFound = mayDecide && canEditModule(localUser, 'pipeline')
  const lockedBecause: string | null = activeRecord != null && activeRecord.partition !== 'live'
    ? 'This is a demo record — it is read-only. Nothing here can be decided.'
    : !mayDecide
      ? 'Your role can read the Data Intelligence Review but not decide anything in it — Intake edit access is needed.'
      : active?.source === 'discovery' && !mayAddFound
        ? 'Adding or dismissing a found datapoint also needs Pipeline edit access.'
        : null
  const readOnly = lockedBecause != null
  // A found datapoint's card is drawn outside the disabled fieldset: its counts
  // open to their evidence and its check re-reads the source, which reading
  // the row allows. Only its decisions — Add, Not a datapoint and the fields
  // they send — are off, and the card turns those off itself.
  const discovered = active?.source === 'discovery' ? active.detail?.discovered : undefined
  // A refused Add or dismissal is said on the card it was refused for; once
  // the reviewer is elsewhere — the row decided since and gone — at the foot.
  const cardError = discovered && error && errorFor === active?.id ? error : null

  const select = (id: string) => {
    setTouched(true)
    setSelectedId(id)
    setPending(null)
    setError(null)
    setErrorFor(null)
    setNotice(null)
  }
  const goRel = (delta: number) => {
    if (allItems.length === 0) return
    select(allItems[(Math.max(activeIndex, 0) + delta + allItems.length) % allItems.length].id)
  }

  // ---- pointing the engine --------------------------------------------------

  const runLocate = async (
    item: DataReviewItem,
    opts: { sha256?: string; cell?: string; lineNo?: number } = {},
  ) => {
    if (!item.ingestionId || !item.field) return
    setSearching(item.id)
    setError(null)
    try {
      const result = await ingestionService.locate(item.ingestionId, { field: item.field, ...opts })
      setLocated((prev) => ({ ...prev, [item.id]: result.candidates ?? [] }))
      setSearchedIn((prev) => ({ ...prev, [item.id]: result.searched ?? [] }))
    } catch {
      setError('The search could not run. Please retry.')
    } finally {
      setSearching(null)
    }
  }

  const openSource = (item: DataReviewItem, sha: string, anchor: SourceAnchor | null) => {
    const record = records.find((r) => r.ingestionId === item.ingestionId)
    // The viewer is addressed by content hash, within this record's own files:
    // a citation to a file it does not hold has nothing to open.
    if (!record || !record.rawFilePaths.some((f) => f.sha256 === sha)) return
    setViewer({ ingestionId: record.ingestionId, files: record.rawFilePaths, sha, anchor, designate: null })
  }

  const designate = (item: DataReviewItem) => {
    const record = records.find((r) => r.ingestionId === item.ingestionId)
    const first = record?.rawFilePaths[0]?.sha256
    if (!record || !first || !item.field) return
    setViewer({
      ingestionId: record.ingestionId, files: record.rawFilePaths, sha: first, anchor: null,
      designate: { itemId: item.id, field: item.field, label: item.label },
    })
  }

  // ---- deciding -------------------------------------------------------------

  /** One verb on one row. Returns whether it was recorded. A refusal because a
   *  person decided the row stays on screen as a question, not an error. */
  const run = async (
    item: DataReviewItem,
    answer: Answer,
    options: { override?: boolean; targetId?: string } = {},
  ): Promise<boolean> => {
    setBusy(item.id)
    setError(null)
    setErrorFor(null)
    setNotice(null)
    setPending(null)
    // Next comes the next decision owed, then anything else still open.
    const after = (list: DataReviewItem[]) => {
      const at = list.findIndex((i) => i.id === item.id)
      return (at >= 0 ? list[at + 1] : undefined) ?? list.filter((i) => i.id !== item.id)[0]
    }
    const later = after(owedItems) ?? after(openItems)
    try {
      const result = await dataReview.decide({
        itemId: options.targetId ?? item.id,
        ...answer,
        note: note.trim() || undefined,
        overrideHumanDecision: options.override || undefined,
      })
      setValueDraft((d) => ({ ...d, [item.id]: '' }))
      setReasonDraft((d) => ({ ...d, [item.id]: '' }))
      setNote('')
      await invalidateAfterDecision(queryClient, item.loanId ?? loanId)
      setNotice(describeResult(item, result))
      if (answer.verb !== 'withdraw') {
        setDecidedCount((n) => n + 1)
        setTouched(true)
        setSelectedId(later?.id ?? null)
      }
      return true
    } catch (e) {
      const refusal = refusalOf(e)
      if (refusal.status === 409 && (refusal.code === 'human_decided' || refusal.overridable)) {
        // A person's decision, or a value a person set on the deal: a question
        // for the reviewer — leave it, or overrule it by name — not an error.
        setPending({ item, answer, refusal })
      } else {
        setError(refusal.message)
        setErrorFor(item.id)
        // Somebody answered it since this list was read — show what is true now.
        if (refusal.code === 'already_settled') void invalidateAfterDecision(queryClient, item.loanId ?? loanId)
      }
      return false
    } finally {
      setBusy(null)
    }
  }

  /** The checklist row's answer, when what a person decided is on the checklist
   *  but the question in front of the reviewer is the engine's proposal. */
  const withdrawTarget = (item: DataReviewItem) =>
    item.source === 'review' ? item.id : `review:${item.ingestionId}:${item.field}`

  const decideReview = (item: DataReviewItem) => (
    _field: string,
    kind: 'confirm' | 'correct' | 'accept_located' | 'unavailable',
    value?: unknown,
    provenance?: Provenance | null,
    reason?: string,
  ) => {
    if (kind === 'confirm') return void run(item, { verb: 'confirm', provenance })
    if (kind === 'unavailable') return void run(item, { verb: 'unavailable', reason })
    return void run(item, {
      verb: 'correct', value, provenance: provenance ?? undefined, located: kind === 'accept_located',
    })
  }

  // Confirm & Next is the checklist's own shortcut: it confirms the value as
  // read for THIS row, and a recorded decision moves on to the next. A refusal
  // or a failed save leaves the reviewer where they are.
  const canConfirm = Boolean(
    active && !readOnly && active.source === 'review' && active.actions.includes('confirm'),
  )
  const confirmAndNext = () => {
    if (!active || !canConfirm) return
    void run(active, { verb: 'confirm', provenance: active.detail?.reviewItem?.provenance })
  }

  const hasDirtyDrafts = Boolean(
    note.trim()
    || Object.values(valueDraft).some((v) => v.trim())
    || Object.values(reasonDraft).some((v) => v.trim()),
  )
  const requestClose = () => {
    if (hasDirtyDrafts && !window.confirm('Discard the unsaved Data Intelligence Review text?')) return
    onClose()
  }
  /**
   * A deal link on a found datapoint, followed from inside the review. This
   * dialog holds the page inert, so whatever the link opened in it — the
   * dossier over /pipeline, where the route does not change — sat under the
   * review with every click falling through to it. The review closes first; a
   * deal then opens in the app's dossier where the reviewer is, and an intake
   * record is left to the link, which navigates to Intake with its review
   * asked for. Returns whether the link should still navigate.
   */
  const followEvidence = (target: EvidenceTarget): boolean => {
    if (hasDirtyDrafts && !window.confirm('Discard the unsaved Data Intelligence Review text?')) return false
    onClose()
    if (!target.loanId) return true
    useDealDossierStore.getState().openDossier(target.loanId)
    return false
  }
  const openDeal = (id: string, tab: 'underwriting' | 'overview') => {
    // The dossier lives behind this dialog, which holds the page inert, so the
    // review closes first.
    onClose()
    useDealDossierStore.getState().openDossier(id, tab)
  }

  // Arrow keys move through the list and Enter confirms a checklist row that
  // has a value. Held in a ref the listener reads, so one listener lives for
  // the panel's life and always sees the current row.
  const onKey = useRef<(event: KeyboardEvent) => void>(() => {})
  useEffect(() => {
    onKey.current = (event) => {
      if (viewer) return
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"]')) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        goRel(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        goRel(-1)
      } else if (event.key === 'Enter' && canConfirm && busy !== active?.id) {
        event.preventDefault()
        confirmAndNext()
      }
    }
  })
  useEffect(() => {
    const listener = (event: KeyboardEvent) => onKey.current(event)
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [])

  const preview = active ? previewTarget(active) : { sha: null, anchor: null }
  const previewSha = preview.sha
    ?? sourceFiles.find((f) => f.kind === 'email_source')?.sha256
    ?? sourceFiles[0]?.sha256
    ?? null
  const initials = (localUser?.name || 'Reviewer').split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase()
  const total = owedItems.length
  const reviewItem: ReviewItem | null = useMemo(() => {
    const base = active?.detail?.reviewItem
    if (!base || active?.field !== 'deal_name' || (base.candidates?.length ?? 0) > 0) return base ?? null
    // The subject line's own suggestions, offered when the checklist carried none.
    const names = (active.options ?? []).map((o) => ({ value: o.value })) as ReviewCandidate[]
    return names.length ? { ...base, candidates: names } : base
  }, [active])

  const header = (
    <div className="flex flex-shrink-0 items-center justify-between border-b border-stone-800 px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <ShieldQuestion size={14} className="shrink-0 text-amber-400" />
        <span className="text-[13px] font-medium text-stone-100">Data Intelligence Review</span>
        {title && <span className="truncate text-[11px] text-stone-500">{title}</span>}
        {/* This record's, or this deal's — never the desk's, which is what the
            Intake tab's badge counts. The same kind of count: decisions owed. */}
        <span className="shrink-0 rounded border border-amber-400/25 bg-amber-400/10 px-2 py-0.5 text-[10px] text-amber-200">
          {plural(total, 'decision')} owed on this {ingestionId ? 'record' : 'deal'}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-[11px] text-stone-400">
          <button type="button" onClick={() => goRel(-1)} className="rounded p-0.5 hover:bg-white/5" aria-label="Previous question">
            <ChevronLeft size={14} />
          </button>
          <span>
            {active && stepOf.has(active.id)
              ? `${stepOf.get(active.id)} of ${total} owed`
              : `${activeIndex >= 0 ? activeIndex + 1 : 0} of ${allItems.length}`}
          </span>
          <button type="button" onClick={() => goRel(1)} className="rounded p-0.5 hover:bg-white/5" aria-label="Next question">
            <ChevronRight size={14} />
          </button>
        </div>
        <button type="button" onClick={requestClose} className="text-stone-500 hover:text-stone-300" title="Close" aria-label="Close Data Intelligence Review">
          <X size={15} />
        </button>
      </div>
    </div>
  )

  const loadFailure = query.isError ? refusalOf(query.error).message : null

  return (
    <>
      <DialogShell label="Data Intelligence Review" onClose={requestClose} width="landscape" header={header}>
        <div className="flex min-h-0 flex-1">
          <div className="flex w-[280px] shrink-0 flex-col border-r border-stone-800">
            <div className="min-h-0 flex-1 overflow-y-auto py-1" data-testid="review-rail">
              {groups.map(({ record, owed, notOwed, decided }) => (
                <div key={record.ingestionId}>
                  {groups.length > 1 && (
                    <RailHeading>{record.subject ?? record.ingestionId}</RailHeading>
                  )}
                  {owed.map((item) => (
                    <RailRow
                      key={item.id} item={item} active={item.id === active?.id}
                      step={stepOf.get(item.id)} total={total} onSelect={() => select(item.id)}
                    />
                  ))}
                  {notOwed.length > 0 && (
                    <RailHeading>
                      {`Listed, not owed · ${notOwed.reduce((n, g) => n + g.items.length, 0)}`}
                    </RailHeading>
                  )}
                  {notOwed.map(({ note, items }) => (
                    <div key={note ?? 'no-note'}>
                      {note && (
                        <p className="px-3 pb-0.5 pt-1 text-[9.5px] leading-snug text-stone-500">{note}</p>
                      )}
                      {items.map((item) => (
                        <RailRow
                          key={item.id} item={item} active={item.id === active?.id}
                          step={undefined} total={total} onSelect={() => select(item.id)}
                        />
                      ))}
                    </div>
                  ))}
                  {decided.length > 0 && <RailHeading>Decided by a person</RailHeading>}
                  {decided.map((item) => (
                    <RailRow
                      key={item.id} item={item} active={item.id === active?.id}
                      step={undefined} total={total} onSelect={() => select(item.id)}
                    />
                  ))}
                </div>
              ))}
              {discovery && (!discovery.available || found.length > 0) && (
                <div>
                  <RailHeading>Found new datapoints{discovery.available ? ` · ${found.length}` : ''}</RailHeading>
                  {discovery.available ? found.map((item) => (
                    <RailRow
                      key={item.id} item={item} active={item.id === active?.id}
                      step={stepOf.get(item.id)} total={total} onSelect={() => select(item.id)}
                    />
                  )) : (
                    <p className="px-3 py-1.5 text-[10.5px] leading-relaxed text-stone-500">{discovery.reason}</p>
                  )}
                </div>
              )}
              {!query.isLoading && !loadFailure && allItems.length === 0 && (
                <p className="px-3 py-4 text-[11px] text-stone-500">
                  {noRecord
                    ? 'No open intake record stands behind this deal, so there is no package to review. A value is changed on the deal itself.'
                    : 'Nothing is waiting on a person here — every datapoint is established.'}
                </p>
              )}
            </div>
            <p className="border-t border-stone-800 px-3 py-2 text-[9px] text-stone-600">
              Ranked: what the pipeline shows first, then the questions the sources disagree on.
              Each one is answered on its own.
            </p>
          </div>

          <div className="min-w-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {query.isLoading && <p className="text-[11px] text-stone-500">Reading the open questions…</p>}
            {loadFailure && (
              <div role="alert" className="rounded border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
                {loadFailure}{' '}
                <button type="button" onClick={() => void query.refetch()} className="underline">Retry</button>
              </div>
            )}
            {lockedBecause && (
              <div role="note" className="rounded border border-stone-600 bg-stone-900 px-3 py-2 text-[11px] text-stone-300">
                {lockedBecause}
              </div>
            )}
            {activeNotOwed && (
              <div
                data-testid="not-owed-note"
                className="rounded border border-stone-700 bg-stone-900/60 px-3 py-2 text-[11px] leading-relaxed text-stone-400"
              >
                <span className="font-medium text-stone-300">Not a decision owed.</span>{' '}
                {active?.bucketNote ?? 'It is listed with its evidence and can still be answered here.'}
              </div>
            )}
            {(pending || !discovered) && (
              <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-3 border-0 p-0">
                {pending && (
                  <OverridePrompt
                    refusal={pending.refusal}
                    busy={busy != null}
                    onLeave={() => setPending(null)}
                    onWithdraw={() => void run(
                      pending.item, { verb: 'withdraw' }, { targetId: withdrawTarget(pending.item) },
                    )}
                    onOverrule={() => void run(pending.item, pending.answer, { override: true })}
                  />
                )}
                {active?.source === 'review' && reviewItem && (
                  <DatapointPanel
                    key={active.id}
                    item={reviewItem}
                    actions={active.actions}
                    located={located[active.id] ?? []}
                    searchedIn={searchedIn[active.id]}
                    searching={searching === active.id}
                    busy={busy === active.id}
                    draft={valueDraft[active.id] ?? ''}
                    onDraft={(v) => setValueDraft((d) => ({ ...d, [active.id]: v }))}
                    reasonDraft={reasonDraft[active.id] ?? ''}
                    onReasonDraft={(v) => setReasonDraft((d) => ({ ...d, [active.id]: v }))}
                    sourceFiles={sourceFiles}
                    scopeSha={scopeSha}
                    onScopeSha={setScopeSha}
                    onOpenSource={(sha, anchor) => openSource(active, sha, anchor)}
                    onDesignate={active.actions.includes('point') ? () => designate(active) : undefined}
                    onLocate={(_field, opts) => void runLocate(active, opts)}
                    onDecide={decideReview(active)}
                    onWithdraw={() => void run(active, { verb: 'withdraw' })}
                  />
                )}
                {active?.source === 'reparse' && (
                  <ReparseItemPanel
                    key={active.id}
                    item={active}
                    busy={busy === active.id}
                    value={valueDraft[active.id] ?? ''}
                    onValue={(v) => setValueDraft((d) => ({ ...d, [active.id]: v }))}
                    reason={reasonDraft[active.id] ?? ''}
                    onReason={(v) => setReasonDraft((d) => ({ ...d, [active.id]: v }))}
                    onDecide={(answer) => void run(active, answer)}
                    onOpenSource={(sha, anchor) => openSource(active, sha, anchor)}
                  />
                )}
                {active?.source === 'reconcile' && (
                  <NeedsPersonPanel
                    key={active.id}
                    item={active}
                    busy={busy === active.id}
                    value={valueDraft[active.id] ?? ''}
                    onValue={(v) => setValueDraft((d) => ({ ...d, [active.id]: v }))}
                    reason={reasonDraft[active.id] ?? ''}
                    onReason={(v) => setReasonDraft((d) => ({ ...d, [active.id]: v }))}
                    onDecide={(answer) => void run(active, answer)}
                    onOpenDeal={openDeal}
                  />
                )}
              </fieldset>
            )}
            {active && discovered && (
              <div className="space-y-2">
                <p className="text-[10.5px] leading-relaxed text-stone-400">{active.question}</p>
                <ul>
                  <DiscoveredRow
                    key={active.id}
                    row={discovered}
                    busy={busy === active.id}
                    disabled={readOnly}
                    onFollow={followEvidence}
                    onOpenFile={(source) => setViewer({
                      ingestionId: source.ingestionId,
                      files: [{ filename: source.filename, kind: 'attachment', sha256: source.sha256 }],
                      sha: source.sha256,
                      anchor: source.anchor,
                      designate: null,
                    })}
                    refusal={cardError}
                    onAccept={(edits) => void run(active, { verb: 'accept', discovery: edits })}
                    onDismiss={() => void run(active, { verb: 'dismiss' })}
                  />
                </ul>
                <p className="text-[9px] text-stone-600">
                  Adding a new field changes what every deal records, so it is always a person’s
                  decision; the engine only ever teaches the parser a word for a field that exists.
                </p>
              </div>
            )}
            {!active && !query.isLoading && !loadFailure && (
              <div className="flex items-center gap-2 rounded border border-stone-700 bg-stone-900 px-3 py-2 text-[11px] text-stone-400">
                {noRecord ? (
                  'There is nothing here to answer.'
                ) : (
                  <>
                    <Check size={13} className="text-emerald-400" />
                    Every question here is answered.
                  </>
                )}
              </div>
            )}
          </div>

          <div className="hidden w-[340px] shrink-0 flex-col border-l border-stone-800 p-3 lg:flex">
            <div className="mb-2 font-mono text-[9px] uppercase tracking-widest text-stone-500">Source preview</div>
            {active?.ingestionId && sourceFiles.length > 0 ? (
              <SourcePreviewPane
                ingestionId={active.ingestionId}
                files={sourceFiles}
                activeSha={previewSha}
                anchor={preview.anchor}
                onOpenFull={(sha) => openSource(active, sha, preview.anchor)}
              />
            ) : (
              <p className="text-[11px] text-stone-500">
                {active?.source === 'discovery'
                  ? 'A found datapoint is read across many deals; its sightings are listed on the row.'
                  : 'No stored source files on this record.'}
              </p>
            )}
            <div className="mt-3 rounded border border-stone-800 bg-black/30 px-3 py-2">
              <div className="text-[9px] uppercase tracking-widest text-stone-600">Need help finding the answer?</div>
              <ul className="mt-1 space-y-1 text-[10px] text-stone-400">
                <li>Find it — re-run the engine on this datapoint across the package.</li>
                <li>Point at it — open the source and click the exact cell or line.</li>
                <li>Enter a value — type the correct figure under the card.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-3 border-t border-stone-800 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400/15 text-[10px] text-amber-200">
              {initials}
            </span>
            <div>
              <div className="text-[11px] text-stone-200">{localUser?.name || 'Reviewer'}</div>
              <div className="text-[9px] text-stone-600">
                Audit trail · {decidedCount === 0 ? 'No changes yet.' : `${decidedCount} decided`}
              </div>
            </div>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={readOnly}
            placeholder="Context recorded with the next decision — optional"
            className="input flex-1 text-[11px] disabled:opacity-50"
          />
          <button type="button" onClick={() => goRel(1)} className="btn-secondary px-3 py-1.5 text-[11px]">
            Skip for now
          </button>
          <button
            type="button"
            disabled={!canConfirm || busy === active?.id}
            onClick={() => confirmAndNext()}
            className="btn-primary px-3 py-1.5 text-[11px] disabled:opacity-40"
          >
            Confirm & Next
          </button>
        </div>

        {error && !cardError && (
          <div role="alert" className="flex flex-shrink-0 items-center gap-2 border-t border-red-500/20 bg-red-500/10 px-4 py-2 text-[11px] text-red-300">
            <AlertTriangle size={13} /> {error}
          </div>
        )}
        {notice && !error && (
          <div role="status" className="flex flex-shrink-0 items-center gap-2 border-t border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-[11px] text-emerald-300">
            <Check size={13} /> {notice}
          </div>
        )}
      </DialogShell>

      {viewer && typeof document !== 'undefined' && createPortal(
        <div role="dialog" aria-modal="true" aria-label="Source document">
          <SourceViewerDialog
            ingestionId={viewer.ingestionId}
            files={viewer.files}
            initialSha={viewer.sha}
            anchor={viewer.anchor}
            designateFor={viewer.designate?.label ?? null}
            onDesignate={viewer.designate ? (place) => {
              const target = viewer.designate!
              const row = allItems.find((i) => i.id === target.itemId)
              setViewer(null)
              select(target.itemId)
              if (row) {
                void runLocate(row, {
                  sha256: place.sha256,
                  cell: place.cell ? (place.sheet ? `${place.sheet}!${place.cell}` : place.cell) : undefined,
                  lineNo: place.lineNo,
                })
              }
            } : undefined}
            onClose={() => setViewer(null)}
          />
        </div>,
        document.body,
      )}
    </>
  )
}
